import React, { useState, useEffect, useRef } from 'react';
import '../styles/OrderPage.css';
import { createOrder, getUserOrders , getUserOrdersByPhone} from '../services/orders';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, getUserStats } from '../services/userProfile';
import { Timestamp, serverTimestamp } from 'firebase/firestore';
import { usePoints } from '../hooks/usePoints';
// وظائف أساسية للأمان
const security = {
  // تنظيف المدخلات من الرموز الخاصة مع السماح بالمسافات والحروف العربية
  sanitizeInput: (input) => {
    if (typeof input !== 'string') return input;
    // السماح فقط بالحروف العربية والإنجليزية والأرقام والمسافات والرموز المعتادة
    return input.replace(/[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\w\s\-_.,!?@()]/g, '');
  },

  // التحقق من رقم الهاتف المصري
  validatePhone: (phone) => {
    return /^01[0125][0-9]{8}$/.test(phone);
  }
};

// List of all available locations
const locations = [
  'السابعه',
  'الجمعيات',
  'الخامسه',
  'التاسعه',
  'شارع مصر',
  'شبين',
  'البهتيني',
  'البحري',
  'البحري بلابسه',
  'حي الاسره',
  'الزهور',
  'الدايري',
  'كارفور',
  'توليب',
  'نمره 6',
  'التجاري',
  'ش زايد',
  'فوكس',
  'المنتزه',
  'الغابه',
  'التبه',
  'الجامعه القديمه',
  'التلاتيني',
  'نفق تحيا مصر',
  'الخيمه الملكيه',
  'كيلو 2',
  'كيلو 7',
  'كيلو 9',
  'كيلو 11',
  'ابو خليفه',
  'ابو صوير',
  'التل الكبير',
  'نفيشه',
  'ابو عطوه',
  'الزغابه',
  'الكاكولا',
  'ابراهيم سلامه',
  'حي السلام',
  'محطه الجديده',
  'سعد زغلول',
  'عراشيه مصر',
  'البلاجات',
  'البهتيني',
  'التعاون',
  'فايد',
  'العشريني',
  'الشهداء',
  'الجعيص',
  'شل',
  'المنايف',
  'ابو صوير',
  'حي الاسراء',
  'الحلوس',
  'طريق بورسعيد',
  'البركه',
  'الدايري',
  'ابو شحاته',
  'شارع المستشفي',
  'شارع مصر',
  'شارع اسكندريه',
  'الدوحه',
  'الارسال',
  'الجندول',
  'النورس',
  'ابو ادم',
  'عرابي',
  'الحرفي',
  'عمر افندي',
  'الاستثمار',
  'المستقبل',
  'الوصفيه',
  'ابو سلطان',
  'محمد علي',
  'سوق بورسعيد',
  'ميدان اندونيسيا',
  'ميدان مرادونا'
].sort();

const LocationSelect = ({ value, onChange, placeholder, name, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLocations, setFilteredLocations] = useState(locations);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchValue = searchTerm.trim();
    if (searchValue) {
      const filtered = locations.filter(location =>
        location.includes(searchValue)
      );
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations(locations);
    }
  }, [searchTerm]);

  const handleSelect = (location) => {
    onChange({ target: { name, value: location } });
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="location-select" ref={dropdownRef}>
      <div className="location-input-wrapper">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            setSearchTerm(newValue);
            onChange({ target: { name, value: newValue } });
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          id={id}
          dir="rtl"
        />
      </div>
      {isOpen && (
        <div className="location-dropdown">
          {filteredLocations.map((location, index) => (
            <div
              key={index}
              className="location-option"
              onClick={() => handleSelect(location)}
            >
              {location}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// مكون التنبيهات - يظهر لمدة 3 ثواني فقط
const Alert = ({ type, message, onClose }) => {
  useEffect(() => {
    // التأكد من وجود رسالة
    if (message && message.trim()) {
      // تعيين مؤقت لإغلاق التنبيه بعد 3 ثواني
      const timer = setTimeout(() => {
        onClose();
      }, 3000);

      // تنظيف المؤقت عند إزالة المكون
      return () => clearTimeout(timer);
    }
  }, [message, onClose]); // تحديث عند تغيير الرسالة أو دالة الإغلاق

  // لا نعرض التنبيه إذا كانت الرسالة فارغة
  if (!message || !message.trim()) {
    return null;
  }

  return (
    <div className={`alert-overlay ${type}`}>
      <div className="alert-content">
        <div className="alert-icon">
          {type === 'success' && '✓'}
          {type === 'error' && '✕'}
          {type === 'warning' && '⚠'}
          {type === 'info' && 'ℹ'}
        </div>
        <div className="alert-message">{message}</div>
      </div>
    </div>
  );
};

const OrderPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [cooldownTimer, setCooldownTimer] = useState(0);
  const [orderCount, setOrderCount] = useState(0);
  const [userPoints, setUserPoints] = useState(0);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [receipts, setReceipts] = useState([]);

  // Load initial orders using useEffect
  useEffect(() => {
    const loadInitialOrders = async () => {
      try {
        const savedUserData = localStorage.getItem('zedorderUserData');
        if (savedUserData) {
          const { customerPhone } = JSON.parse(savedUserData);
          if (customerPhone && customerPhone.length === 11) {
            const result = await getUserOrdersByPhone(customerPhone);
            if (result.status === 'success' && Array.isArray(result.data)) {
              const formattedOrders = result.data.map(order => ({
                ...order,
                time: new Date(order.createdAt?.seconds * 1000).toLocaleTimeString('ar-EG'),
                date: new Date(order.createdAt?.seconds * 1000).toLocaleDateString('ar-EG')
              }));
              setReceipts(formattedOrders);
            }
          }
        }
      } catch (error) {
        console.error('Error loading initial orders:', error);
      }
    };

    loadInitialOrders();
  }, []);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [hasQuickOrderSetup, setHasQuickOrderSetup] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showReceiptsSidebar, setShowReceiptsSidebar] = useState(false);
  const [orderData, setOrderData] = useState({
    customerName: '',
    customerPhone: '',
    pickupLocation: '',
    deliveryLocation: '',
    notes: ''
  });
  
  const [quickOrderData, setQuickOrderData] = useState({
    customerName: '',
    customerPhone: '',
    defaultPickupLocation: ''
  });

  // Authenticated user (if any)
  const { user } = useAuth();

  // Cooldown timer effect
  useEffect(() => {
    let timer;
    if (cooldownTimer > 0) {
      timer = setTimeout(() => {
        setCooldownTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldownTimer]);

  // Load previous orders effect
  useEffect(() => {
    const loadPreviousOrders = async () => {
      try {
        // محاولة استرجاع البيانات من التخزين المحلي أولاً
        const savedUserData = localStorage.getItem('zedorderUserData');
        let phoneToUse = '';

        if (savedUserData) {
          const parsedData = JSON.parse(savedUserData);
          phoneToUse = parsedData.customerPhone;
        }

        // استخدم رقم الهاتف من النموذج إذا كان موجوداً
        if (orderData.customerPhone && orderData.customerPhone.length === 11) {
          phoneToUse = orderData.customerPhone;
        }

        // تحميل الطلبات إذا كان لدينا رقم هاتف صحيح
        if (phoneToUse && phoneToUse.length === 11) {
          const result = await getUserOrdersByPhone(phoneToUse);
          if (result.status === 'success' && Array.isArray(result.data)) {
            const formattedOrders = result.data.map(order => ({
              ...order,
              time: new Date(order.createdAt?.seconds * 1000).toLocaleTimeString('ar-EG'),
              date: new Date(order.timestamp?.seconds * 1000).toLocaleDateString('ar-EG')
            }));
            setReceipts(formattedOrders);
          }
        }
      } catch (error) {
        console.error('Error loading previous orders:', error);
      }
    };

    loadPreviousOrders();
  }, [orderData.customerPhone]);

  // Prefill order form and load user points from Supabase when user is logged in
  useEffect(() => {
    if (!user) return;

    const loadUserData = async () => {
      try {
        // Load user profile
        const res = await getUserProfile(user.id);
        if (res.status === 'success' && res.data) {
          const profile = res.data;
          setOrderData(prev => ({
            customerName: prev.customerName || (profile.full_name || ''),
            customerPhone: prev.customerPhone || (profile.phone || ''),
            pickupLocation: prev.pickupLocation || (profile.address || ''),
            deliveryLocation: prev.deliveryLocation || '',
            notes: prev.notes || ''
          }));

          // Load user points
          if (profile.phone) {
            const pointsResult = await usePoints.getUserPoints(profile.phone);
            if (pointsResult.status === 'success') {
              setUserPoints(pointsResult.points);
            }
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err);
      }
    };

    loadUserData();

    // تحديث النقاط كل دقيقة
    const intervalId = setInterval(async () => {
      try {
        const profile = await getUserProfile(user.id);
        if (profile.status === 'success' && profile.data?.phone) {
          const pointsResult = await usePoints.getUserPoints(profile.data.phone);
          if (pointsResult.status === 'success') {
            setUserPoints(pointsResult.points);
          }
        }
      } catch (err) {
        console.error('Error updating points:', err);
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, [user]);

  // Calculate cooldown time based on order count
  const calculateCooldown = () => {
    switch(orderCount) {
      case 0: return 30;
      case 1: return 40;
      case 2: return 50;
      default: return 60;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // معالجة رقم الهاتف
    if (name === 'customerPhone') {
      // السماح فقط بالأرقام وبحد أقصى 11 رقم
      const numbersOnly = value.replace(/[^0-9]/g, '').slice(0, 11);
      setOrderData(prev => ({
        ...prev,
        [name]: numbersOnly
      }));
    } else if (name === 'notes' || name === 'customer_name') {
      // السماح بجميع الأحرف والمسافات في الملاحظات واسم العميل
      setOrderData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      // تنظيف المدخلات الأخرى من الرموز الخاصة
      const cleanValue = security.sanitizeInput(value);
      setOrderData(prev => ({
        ...prev,
        [name]: cleanValue
      }));
    }
  };

  const showAlert = (type, message) => {
    // عرض التنبيه الجديد
    setAlert({ type, message });
    
    // إزالة التنبيه تلقائياً بعد 3 ثواني
    setTimeout(() => {
      setAlert(null);
    }, 3000);
  };

  const validateCurrentStep = () => {
    // Only validate when user tries to proceed to next step
    if (currentStep === 1) {
      const errors = [];
      if (!orderData.customerName.trim()) {
        errors.push('يرجى إدخال اسمك الكامل');
      }
      if (!orderData.customerPhone.trim()) {
        errors.push('يرجى إدخال رقم هاتفك');
      } else if (!/^[0-9]{11}$/.test(orderData.customerPhone.trim())) {
        errors.push('يرجى إدخال رقم هاتف صحيح مكون من 11 رقم');
      }
      
      if (errors.length > 0) {
        showAlert('error', errors[0]);
        return false;
      }
    }
    if (currentStep === 2) {
      if (!orderData.pickupLocation.trim()) {
        showAlert('error', 'يرجى تحديد موقع الاستلام');
        return false;
      }
      if (orderData.deliveryLocation.trim() && orderData.pickupLocation === orderData.deliveryLocation) {
        showAlert('error', 'موقع الاستلام والتوصيل لا يمكن أن يكونا نفس الموقع');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      // حفظ البيانات في التخزين المحلي عند الانتقال من الخطوة الأولى
      if (currentStep === 1) {
        try {
          localStorage.setItem('zedorderUserData', JSON.stringify({
            customerName: orderData.customerName.trim(),
            customerPhone: orderData.customerPhone.trim(),
            pickupLocation: orderData.pickupLocation.trim() || ''
          }));
        } catch (error) {
          console.error('Error saving user data:', error);
        }
      }

      // عند الانتقال إلى الخطوة الثالثة (تأكيد الطلب)
      if (currentStep === 2) {
        setShowReceipt(false);
        setSelectedReceipt(null);
      }

      // الانتقال إلى الخطوة التالية
      setCurrentStep(prev => prev + 1);
    }
  };

  const saveQuickOrderSetup = () => {
    if (!quickOrderData.customerName || !quickOrderData.defaultPickupLocation) {
      showAlert('error', 'الرجاء إكمال جميع البيانات المطلوبة');
      return;
    }

    if (!quickOrderData.customerPhone || quickOrderData.customerPhone.length !== 11) {
      showAlert('error', 'يرجى إدخال رقم هاتف صحيح مكون من 11 رقم');
      return;
    }

    localStorage.setItem('zedorderQuickSetup', JSON.stringify(quickOrderData));
    setHasQuickOrderSetup(true);
    setShowQuickSetup(false);
    showAlert('success', 'تم تخصيص الطلب السريع بنجاح! 🎉');
  };

  const quickOrder = async () => {
    if (cooldownTimer > 0) {
      showAlert('warning', `الرجاء الانتظار ${cooldownTimer} ثانية قبل تقديم طلب جديد`);
      return;
    }

    const savedData = localStorage.getItem('zedorderQuickSetup');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        // تعيين البيانات في نموذج الطلب
        setOrderData({
          customerName: parsedData.customerName,
          customerPhone: parsedData.customerPhone,
          pickupLocation: parsedData.defaultPickupLocation,
          deliveryLocation: '',
          notes: ''
        });
        
        // الانتقال إلى صفحة تأكيد الطلب
        setCurrentStep(3);
        
      } catch (error) {
        showAlert('error', 'حدث خطأ أثناء تحميل البيانات المحفوظة');
      }
    } else {
      setShowQuickSetup(true);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const generateOrderNumber = () => {
    return 'ZED' + Date.now().toString().slice(-6);
  };

  const Receipt = ({ order, onClose, onNewOrder }) => {
    return (
      <div className="receipt-modal">
        <div className="receipt-content">
          <div className="receipt-header">
            <h3>فاتورة الطلب</h3>
            <div className="receipt-number">رقم الطلب: {order.orderNumber}</div>
            <div className="receipt-time">{new Date().toLocaleDateString('ar-EG')} - {new Date().toLocaleTimeString('ar-EG')}</div>
            {order.is_free_order && (
              <div className="receipt-badge">
                <span className="badge badge-success">🎁 طلب مجاني باستخدام النقاط</span>
              </div>
            )}
          </div>
          
          <div className="receipt-body">
            <div className="receipt-section">
              <h4>معلومات العميل</h4>
              <div className="receipt-info">
                <div className="info-row">
                  <span className="info-label">الاسم:</span>
                  <span className="info-value">{order.customerName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">رقم الهاتف:</span>
                  <span className="info-value">{order.customerPhone}</span>
                </div>
              </div>
            </div>

            <div className="receipt-section">
              <h4>تفاصيل التوصيل</h4>
              <div className="receipt-info">
                <div className="info-row">
                  <span className="info-label">موقع الاستلام:</span>
                  <span className="info-value">{order.pickupLocation}</span>
                </div>
                {order.deliveryLocation && (
                  <div className="info-row">
                    <span className="info-label">موقع التوصيل:</span>
                    <span className="info-value">{order.deliveryLocation}</span>
                  </div>
                )}
                {order.notes && (
                  <div className="info-row">
                    <span className="info-label">ملاحظات:</span>
                    <span className="info-value">{order.notes}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="receipt-footer">
            <button className="btn btn-primary btn-new-order" onClick={onNewOrder}>
              طلب جديد
            </button>
            <button className="btn btn-secondary btn-close" onClick={onClose}>
              إغلاق
            </button>
          </div>
        </div>
      </div>
    );
  };

  const validateOrder = () => {
    // التحقق من الاسم
    if (!orderData.customerName.trim()) {
      showAlert('error', 'يرجى إدخال اسمك الكامل');
      return false;
    }

    // التحقق من رقم الهاتف
    if (!security.validatePhone(orderData.customerPhone)) {
      showAlert('error', 'يرجى إدخال رقم هاتف صحيح مكون من 11 رقم');
      return false;
    }

    // التحقق من موقع الاستلام
    if (!orderData.pickupLocation.trim()) {
      showAlert('error', 'يرجى تحديد موقع الاستلام');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // التأكد من أننا في الخطوة الأخيرة
    if (currentStep !== 3) {
      return;
    }
    
    // التحقق من المهلة الزمنية
    if (cooldownTimer > 0) {
      showAlert('warning', `الرجاء الانتظار ${cooldownTimer} ثانية قبل تقديم طلب جديد`);
      return;
    }

    // التحقق من صحة البيانات
    if (!validateOrder()) {
      return;
    }

    // حفظ الطلب في Firebase
    try {
      // توليد رقم الطلب
      const newOrderNumber = generateOrderNumber();
      const newOrder = {
        ...orderData,
        orderNumber: newOrderNumber,
        timestamp: serverTimestamp()
      };
      
      const createdOrder = await createOrder(newOrder);
      
      if (createdOrder.status === 'success') {
        // إضافة إلى الفواتير المحلية
        const orderWithTime = {
          ...newOrder,
          time: new Date().toLocaleTimeString('ar-EG'),
          date: new Date().toLocaleDateString('ar-EG'),
          id: createdOrder.orderId
        };
        
        // تحديث العداد والمؤقت
        setOrderCount(prev => Math.min(prev + 1, 3));
        setCooldownTimer(calculateCooldown());
        
        // عرض رسالة النجاح
        showAlert('success', createdOrder.message || 'تم إرسال طلبك بنجاح، سيتواصل معك المندوب قريباً! 🚀');
        
        // تحديث قائمة الفواتير أولاً
        setReceipts(prev => [orderWithTime, ...prev]);
        
        // ثم إعادة تعيين النموذج
        setTimeout(() => {
          setOrderData({
            customerName: '',
            customerPhone: '',
            pickupLocation: '',
            deliveryLocation: '',
            notes: ''
          });
          setCurrentStep(1);
        }, 1000);
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      showAlert('error', 'حدث خطأ أثناء إرسال الطلب. الرجاء المحاولة مرة أخرى.');
    }
  };

  return (
    <section className="order-section" id="order">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
      
      <div className="container">
        <div className="order-container">
          <div className="order-card">
            <h2 className="order-title">اطلب خدمة التوصيل</h2>
            
            {/* Progress Steps */}
            <div className="progress-container">
              <div className="progress-steps">
                <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
                  <div className="step-number">1</div>
                  <div className="step-label">بيانات الاتصال</div>
                </div>
                <div className="progress-line"></div>
                <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-label">تحديد المواقع</div>
                </div>
                <div className="progress-line"></div>
                <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
                  <div className="step-number">3</div>
                  <div className="step-label">تأكيد الطلب</div>
                </div>
              </div>
            </div>

            {/* Order Form */}
            <form id="orderForm" onSubmit={handleSubmit}>
              {/* Step 1: Contact Information */}
              {/* Receipts Sidebar */}
              {/* Active Receipt Modal */}  
              {/* Step 1: Contact Information */}
              <div className={`form-step ${currentStep === 1 ? 'active' : ''}`}>
                <h3 className="step-title">بيانات الاتصال</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customerName">الاسم بالكامل</label>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={orderData.customerName}
                      onChange={handleInputChange}
                      placeholder="أدخل اسمك بالكامل"
                      required
                      dir="rtl"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="customerPhone">رقم الهاتف</label>
                    <input
                      type="tel"
                      id="customerPhone"
                      name="customerPhone"
                      value={orderData.customerPhone}
                      onChange={handleInputChange}
                      placeholder="أدخل رقم هاتفك (11 رقم)"
                      maxLength="11"
                      pattern="[0-9]{11}"
                      required
                      dir="rtl"
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Location Details */}
              <div className={`form-step ${currentStep === 2 ? 'active' : ''}`}>
                <h3 className="step-title">تفاصيل المواقع</h3>
                <div className="form-group">
                  <label htmlFor="pickupLocation">موقع الاستلام</label>
                  <LocationSelect
                    id="pickupLocation"
                    name="pickupLocation"
                    value={orderData.pickupLocation}
                    onChange={handleInputChange}
                    placeholder="اختر نقطة الاستلام"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="deliveryLocation">موقع التوصيل</label>
                  <LocationSelect
                    id="deliveryLocation"
                    name="deliveryLocation"
                    value={orderData.deliveryLocation}
                    onChange={handleInputChange}
                    placeholder="اختر وجهة التوصيل"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="notes">ملاحظات إضافية</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={orderData.notes}
                    onChange={handleInputChange}
                    placeholder="أي تعليمات خاصة؟"
                    rows="3"
                    dir="rtl"
                  ></textarea>
                </div>
              </div>

              {/* Step 3: Confirmation */}
              <div className={`form-step ${currentStep === 3 ? 'active' : ''}`}>
                <h3 className="step-title">تأكيد الطلب</h3>
                <div className="payment-note">
                  <div className="note-content">
                    <span className="note-icon">💳</span>
                    <p>متاح الدفع عن طريق المحافظ الالكترونيه و InstaPay</p>
                  </div>
                </div>
                <div className="order-summary">
                  <h4 className="summary-title">ملخص الطلب</h4>
                  <div className="summary-item">
                    <span className="summary-label">الاسم:</span>
                    <span className="summary-value" id="summaryName">{orderData.customerName}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">الهاتف:</span>
                    <span className="summary-value" id="summaryPhone">{orderData.customerPhone}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">موقع الاستلام:</span>
                    <span className="summary-value" id="summaryPickup">{orderData.pickupLocation}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">موقع التوصيل:</span>
                    <span className="summary-value" id="summaryDelivery">{orderData.deliveryLocation}</span>
                  </div>
                  {orderData.notes && (
                    <div className="summary-item">
                      <span className="summary-label">ملاحظات:</span>
                      <span className="summary-value" id="summaryNotes">{orderData.notes}</span>
                    </div>
                  )}
                  <div className="confirmation-actions">
                    {user && (
                      <div className="free-order-section mb-3">
                        <button 
                          type="button" 
                          className={`btn ${userPoints * 10 >= 100 ? 'btn-primary' : 'btn-secondary opacity-60'} w-full`}
                          disabled={userPoints * 10 < 100}
                          onClick={async () => {
                            try {
                              // تحديث حالة النقاط للطلب المجاني
                              const updateResult = await usePoints.updatePointsStatus(orderData.customerPhone);
                              if (updateResult.status === 'error') {
                                showAlert('error', updateResult.message);
                                return;
                              }
                              
                              // إنشاء الطلب المجاني
                              const freeOrderResult = await usePoints.createFreeOrder({
                                customerName: orderData.customerName,
                                customerPhone: orderData.customerPhone,
                                pickupLocation: orderData.pickupLocation,
                                deliveryLocation: orderData.deliveryLocation,
                                notes: orderData.notes
                              });

                              if (freeOrderResult.status === 'success') {
                                showAlert('success', 'تم إنشاء الطلب المجاني بنجاح! �');
                                setCurrentStep(1);
                                setOrderData({
                                  customerName: '',
                                  customerPhone: '',
                                  pickupLocation: '',
                                  deliveryLocation: '',
                                  notes: ''
                                });
                              } else {
                                showAlert('error', freeOrderResult.message);
                              }
                            } catch (error) {
                              showAlert('error', 'حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.');
                            }
                          }}
                        >
                          {userPoints *10  >= 100 ? (
                            <>استخدام النقاط للحصول على طلب مجاني (100 نقطة) 🎁</>
                          ) : (
                            <>احصل على طلب مجاني (تحتاج {100 - userPoints * 10} نقطة إضافية) 🎁</>
                          )}
                        </button>
                        <div className="text-sm text-white-600 text-center mt-1">
                          رصيد نقاطك الحالي: {userPoints * 10} نقطة 
                        </div>
                      </div>
                    )}
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setCurrentStep(2)}
                    >
                      تعديل البيانات
                    </button>
                  </div>
                </div>
              </div>

              {/* Form Navigation */}
              <div className="step-actions">
                <div className="step-buttons-group">
                  {currentStep > 1 && (
                    <button 
                      type="button" 
                      className="btn btn-secondary step-prev" 
                      onClick={prevStep}
                    >
                      السابق
                    </button>
                  )}
                  {currentStep < 3 ? (
                    <button 
                      type="button" 
                      className="btn btn-primary step-next" 
                      onClick={nextStep}
                    >
                      التالي
                    </button>
                  ) : (
                    <button 
                      type="button"
                      className="btn btn-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit(e);
                      }}
                    >
                      تأكيد وإرسال الطلب
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderPage;
